import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Plus, Save, Trash2, Link2, Palette, Image as ImageIcon, X } from 'lucide-react';

/**
 * iter126 — Freeform R&D whiteboard.
 * Sticky-note nodes (drag by header), SVG lines between them.
 * No external canvas lib — plain divs + one SVG overlay.
 * State is persisted via PUT /api/rd/projects/{id}/whiteboard
 */
const COLORS = {
  yellow: 'bg-yellow-100 border-yellow-300',
  blue: 'bg-blue-100 border-blue-300',
  pink: 'bg-pink-100 border-pink-300',
  green: 'bg-emerald-100 border-emerald-300',
  purple: 'bg-purple-100 border-purple-300',
  orange: 'bg-orange-100 border-orange-300',
};
const COLOR_KEYS = Object.keys(COLORS);

const genId = () => Math.random().toString(36).slice(2, 10);

export default function RDWhiteboard({ projectId, initialState }) {
  const { api } = useAuth();
  const [nodes, setNodes] = useState(initialState?.nodes || []);
  const [edges, setEdges] = useState(initialState?.edges || []);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connectFrom, setConnectFrom] = useState(null);
  const canvasRef = useRef(null);

  // Drag state
  const dragRef = useRef(null); // {id, offsetX, offsetY}

  useEffect(() => {
    setNodes(initialState?.nodes || []);
    setEdges(initialState?.edges || []);
    setDirty(false);
  }, [projectId]);

  const addNote = () => {
    const n = {
      id: genId(),
      x: 40 + nodes.length * 20,
      y: 40 + nodes.length * 20,
      width: 200,
      height: 120,
      color: COLOR_KEYS[nodes.length % COLOR_KEYS.length],
      text: '',
    };
    setNodes(ns => [...ns, n]);
    setDirty(true);
  };

  const updateNode = (id, patch) => {
    setNodes(ns => ns.map(n => n.id === id ? { ...n, ...patch } : n));
    setDirty(true);
  };

  const removeNode = (id) => {
    setNodes(ns => ns.filter(n => n.id !== id));
    setEdges(es => es.filter(e => e.from_id !== id && e.to_id !== id));
    setDirty(true);
  };

  // iter128 — paste a screenshot anywhere on a note while it's focused.
  const handlePasteOnNode = async (e, nodeId) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type && it.type.startsWith('image/')) {
        e.preventDefault();
        const blob = it.getAsFile();
        if (!blob) continue;
        // Cap at ~500 KB so Mongo docs don't blow up
        if (blob.size > 500 * 1024) {
          toast.error('Image too large (max 500 KB). Please shrink or crop.');
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          updateNode(nodeId, { image_data_url: reader.result, height: Math.max(220, nodes.find(n => n.id === nodeId)?.height || 220) });
          toast.success('Image attached');
        };
        reader.readAsDataURL(blob);
        return;
      }
    }
  };

  const clearImage = (id) => updateNode(id, { image_data_url: null });

  const cycleColor = (id) => {
    const n = nodes.find(x => x.id === id); if (!n) return;
    const idx = COLOR_KEYS.indexOf(n.color || 'yellow');
    const next = COLOR_KEYS[(idx + 1) % COLOR_KEYS.length];
    updateNode(id, { color: next });
  };

  const beginConnect = (id) => {
    if (connectFrom === id) { setConnectFrom(null); return; }
    if (connectFrom) {
      // Finalize edge
      if (connectFrom !== id) {
        setEdges(es => [...es, { id: genId(), from_id: connectFrom, to_id: id }]);
        setDirty(true);
      }
      setConnectFrom(null);
    } else {
      setConnectFrom(id);
    }
  };

  const removeEdge = (eid) => {
    setEdges(es => es.filter(e => e.id !== eid));
    setDirty(true);
  };

  const onNoteMouseDown = (e, node) => {
    // Only drag when grabbing the header (data-drag-handle)
    if (!e.target.closest || !e.target.closest('[data-drag-handle="true"]')) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    dragRef.current = {
      id: node.id,
      offsetX: e.clientX - rect.left - node.x,
      offsetY: e.clientY - rect.top - node.y,
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const nx = Math.max(0, e.clientX - rect.left - dragRef.current.offsetX);
      const ny = Math.max(0, e.clientY - rect.top - dragRef.current.offsetY);
      updateNode(dragRef.current.id, { x: nx, y: ny });
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api().put(`/rd/projects/${projectId}/whiteboard`, { nodes, edges });
      toast.success('Whiteboard saved');
      setDirty(false);
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Edge coords: center of source → center of target
  const centerOf = (id) => {
    const n = nodes.find(x => x.id === id);
    return n ? { cx: n.x + n.width / 2, cy: n.y + n.height / 2 } : null;
  };

  return (
    <Card className="p-4" data-testid="rd-whiteboard-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="font-semibold text-slate-800">Whiteboard</h2>
          <p className="text-xs text-slate-500">Drag by the top strip · click <Link2 className="inline w-3 h-3" /> on two notes to connect · <ImageIcon className="inline w-3 h-3" /> paste a screenshot straight into a note.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addNote} data-testid="rd-whiteboard-add-note"><Plus className="w-4 h-4 mr-1" />Sticky note</Button>
          <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid="rd-whiteboard-save">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : dirty ? 'Save board' : 'Saved'}
          </Button>
        </div>
      </div>

      <div
        ref={canvasRef}
        className="relative w-full border border-dashed border-slate-300 rounded bg-slate-50"
        style={{ height: 600, overflow: 'auto' }}
        data-testid="rd-whiteboard-canvas"
      >
        {/* SVG edges */}
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{ width: '100%', height: '100%' }}
        >
          <defs>
            <marker id="rd-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
            </marker>
          </defs>
          {edges.map(e => {
            const a = centerOf(e.from_id); const b = centerOf(e.to_id);
            if (!a || !b) return null;
            return (
              <g key={e.id}>
                <line
                  x1={a.cx} y1={a.cy} x2={b.cx} y2={b.cy}
                  stroke="#64748b" strokeWidth="1.5"
                  markerEnd="url(#rd-arrow)"
                />
                <circle
                  cx={(a.cx + b.cx) / 2} cy={(a.cy + b.cy) / 2}
                  r="7" fill="#ffffff" stroke="#cbd5e1"
                  style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                  onClick={() => removeEdge(e.id)}
                >
                  <title>Remove edge</title>
                </circle>
                <text
                  x={(a.cx + b.cx) / 2} y={(a.cy + b.cy) / 2 + 3}
                  textAnchor="middle" fontSize="9" fill="#64748b"
                  style={{ pointerEvents: 'none' }}
                >×</text>
              </g>
            );
          })}
        </svg>

        {nodes.map(n => (
          <div
            key={n.id}
            className={`absolute border rounded shadow-sm ${COLORS[n.color] || COLORS.yellow} ${connectFrom === n.id ? 'ring-2 ring-fuchsia-400' : ''}`}
            style={{ left: n.x, top: n.y, width: n.width, height: n.height }}
            onMouseDown={(e) => onNoteMouseDown(e, n)}
            onPaste={(e) => handlePasteOnNode(e, n.id)}
            tabIndex={0}
            data-testid={`rd-whiteboard-note-${n.id}`}
          >
            <div className="flex items-center justify-between px-2 py-1 border-b border-black/5 cursor-move" data-drag-handle="true">
              <button
                className="text-[10px] text-slate-600 hover:text-slate-900"
                onClick={() => cycleColor(n.id)}
                title="Change color"
                data-testid={`rd-note-color-${n.id}`}
              ><Palette className="w-3 h-3" /></button>
              <div className="flex items-center gap-1">
                {n.image_data_url && (
                  <button
                    className="text-[10px] text-slate-600 hover:text-rose-600"
                    onClick={() => clearImage(n.id)}
                    title="Remove image"
                    data-testid={`rd-note-clear-image-${n.id}`}
                  ><X className="w-3 h-3" /></button>
                )}
                <button
                  className={`text-[10px] ${connectFrom === n.id ? 'text-fuchsia-600' : 'text-slate-600 hover:text-fuchsia-600'}`}
                  onClick={() => beginConnect(n.id)}
                  title={connectFrom ? (connectFrom === n.id ? 'Cancel connect' : 'Finish connect here') : 'Start connection'}
                  data-testid={`rd-note-connect-${n.id}`}
                ><Link2 className="w-3 h-3" /></button>
                <button
                  className="text-[10px] text-rose-500 hover:text-rose-700"
                  onClick={() => removeNode(n.id)}
                  title="Delete"
                  data-testid={`rd-note-remove-${n.id}`}
                ><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
            {n.image_data_url && (
              <img
                src={n.image_data_url}
                alt="pasted"
                className="w-full object-contain bg-white/40"
                style={{ maxHeight: Math.max(60, n.height - 70) }}
                data-testid={`rd-note-image-${n.id}`}
                draggable={false}
              />
            )}
            <textarea
              className="w-full bg-transparent px-2 py-1 text-xs resize-none focus:outline-none"
              style={{ height: n.image_data_url ? 44 : `calc(100% - 26px)` }}
              value={n.text}
              onChange={(e) => updateNode(n.id, { text: e.target.value })}
              onPaste={(e) => handlePasteOnNode(e, n.id)}
              placeholder={n.image_data_url ? 'Caption…' : 'Jot something, or paste an image…'}
              data-testid={`rd-note-text-${n.id}`}
            />
          </div>
        ))}

        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
            Empty board. Click <b className="mx-1">Sticky note</b> to drop your first thought.
          </div>
        )}
      </div>
    </Card>
  );
}
